// Game-feel effects: particles (debris/sparks/smoke/dust), one-shot rings
// (spawn-in, pickups), screen shake, and the DOM damage vignette. All state
// lives on the Renderer; these functions spawn, advance, and draw it.

import type { SnapshotDTO, TankDTO } from "../../shared/protocol/index.js";
import { $ } from "../dom.js";
import { state } from "../state.js";
import type { Renderer } from "./Renderer.js";
import { HIT_FLASH_MS, MUZZLE_MS, PARTICLE_CAP, REDUCED_MOTION, type TankFx } from "./types.js";

/** Get-or-create the per-tank transient effect record. */
export function fx(rd: Renderer, id: string): TankFx {
  let f = rd.tankFx.get(id);
  if (!f) {
    f = { muzzleUntil: 0, recoilAt: 0, hitAt: 0, spawnAt: 0 };
    rd.tankFx.set(id, f);
  }
  return f;
}

/** Add screen-shake trauma (world px). No-op under reduced motion. */
export function addShake(rd: Renderer, mag: number): void {
  if (REDUCED_MOTION || mag <= 0) return;
  rd.shakeMag = Math.min(9, rd.shakeMag + mag);
}

/** Shake strength for an effect at (x,y), scaled by distance to the local tank. */
export function shakeFor(snap: SnapshotDTO, x: number, y: number, base: number): number {
  const me = snap.tanks.find((t) => t.id === state.playerId);
  if (!me) return 0;
  const d = Math.hypot(me.x - x, me.y - y);
  return base * Math.max(0, 1 - d / 520);
}

/** Spawn `n` particles bursting radially outward from (x,y). */
export function spawnBurst(
  rd: Renderer,
  x: number,
  y: number,
  n: number,
  nowMs: number,
  opts: {
    color: string;
    speed: number; // px/s (randomized ±50%)
    size: number; // px (randomized ±40%)
    life: number; // ms (randomized ±30%)
    alpha?: number;
    drag?: number;
    grow?: number;
    square?: boolean;
    vx?: number; // added base velocity (e.g. drift with the tank)
    vy?: number;
  }
): void {
  if (REDUCED_MOTION) return;
  const count = Math.min(n, Math.max(0, PARTICLE_CAP - rd.particles.length));
  for (let i = 0; i < count; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = opts.speed * (0.5 + Math.random());
    rd.particles.push({
      x,
      y,
      vx: Math.cos(a) * sp + (opts.vx ?? 0),
      vy: Math.sin(a) * sp + (opts.vy ?? 0),
      size: opts.size * (0.6 + Math.random() * 0.8),
      color: opts.color,
      alpha: opts.alpha ?? 1,
      born: nowMs,
      life: opts.life * (0.7 + Math.random() * 0.6),
      drag: opts.drag ?? 2.5,
      grow: opts.grow ?? 0.4,
      square: opts.square ?? false,
    });
  }
}

/** Debris + smoke for a tank death or explosive blast. */
export function spawnExplosionDebris(rd: Renderer, x: number, y: number, color: string, nowMs: number): void {
  const q = state.quality === "low" ? 0.5 : 1;
  // Hot embers in the tank's color fly furthest.
  spawnBurst(rd, x, y, Math.round(8 * q), nowMs, {
    color,
    speed: 150,
    size: 2.4,
    life: 550,
    drag: 2.2,
    grow: 0.2,
    square: true,
  });
  // Dark shrapnel chunks.
  spawnBurst(rd, x, y, Math.round(6 * q), nowMs, {
    color: state.modernEnabled ? "#334155" : "#3a332a",
    speed: 110,
    size: 2.8,
    life: 650,
    drag: 2.8,
    grow: 0.3,
    square: true,
  });
  // Slow smoke that grows and lingers after the flash is gone.
  spawnBurst(rd, x, y, Math.round(5 * q), nowMs, {
    color: state.modernEnabled ? "rgba(0, 248, 248, 0.2)" : "#6b6257",
    speed: 34,
    size: 5,
    life: 900,
    alpha: 0.4,
    drag: 1.2,
    grow: 2.4,
  });
}

/** Small impact sparks (bullet hit, laser terminus). */
export function spawnSparks(rd: Renderer, x: number, y: number, color: string, n: number, nowMs: number): void {
  spawnBurst(rd, x, y, n, nowMs, {
    color,
    speed: 120,
    size: 1.6,
    life: 320,
    drag: 3,
    grow: 0.2,
  });
}

/** Advance and draw all live particles; drawn inside the fog clip. */
export function drawParticles(rd: Renderer, nowMs: number, dt: number): void {
  if (rd.particles.length === 0) return;
  const { ctx } = rd;
  rd.particles = rd.particles.filter((p) => nowMs - p.born < p.life);
  const damp = (drag: number) => Math.max(0, 1 - drag * dt);
  for (const p of rd.particles) {
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.vx *= damp(p.drag);
    p.vy *= damp(p.drag);
    const age = (nowMs - p.born) / p.life; // 0..1
    const size = Math.max(0.3, p.size * (1 + (p.grow - 1) * age));
    ctx.globalAlpha = (1 - age) * p.alpha;
    ctx.fillStyle = p.color;
    if (p.square) {
      ctx.fillRect(p.x - size, p.y - size, size * 2, size * 2);
    } else {
      ctx.beginPath();
      ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

/** Draw one-shot rings (spawn-in, pickups); drawn inside the fog clip. */
export function drawRings(rd: Renderer, nowMs: number): void {
  if (rd.rings.length === 0) return;
  const { ctx } = rd;
  rd.rings = rd.rings.filter((r) => nowMs - r.born < r.life);
  for (const r of rd.rings) {
    const age = (nowMs - r.born) / r.life; // 0..1
    const ease = 1 - (1 - age) * (1 - age); // ease-out
    ctx.globalAlpha = 1 - age;
    ctx.strokeStyle = r.color;
    ctx.lineWidth = r.width * (1 - age) + 0.5;
    ctx.beginPath();
    ctx.arc(r.x, r.y, Math.max(0.5, r.r0 + (r.r1 - r.r0) * ease), 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

/** Dust puffs kicked up behind moving tanks (skipped on low quality). */
export function spawnDust(rd: Renderer, tanks: TankDTO[], nowMs: number): void {
  if (REDUCED_MOTION || state.quality === "low") return;
  for (const t of tanks) {
    if (!t.alive) {
      rd.lastDust.delete(t.id);
      continue;
    }
    const last = rd.lastDust.get(t.id);
    if (!last) {
      rd.lastDust.set(t.id, { x: t.x, y: t.y, at: nowMs });
      continue;
    }
    if (nowMs - last.at < 90) continue;
    const dx = t.x - last.x;
    const dy = t.y - last.y;
    const d = Math.hypot(dx, dy);
    last.at = nowMs;
    if (d < 4) {
      last.x = t.x;
      last.y = t.y;
      continue;
    }
    // One soft puff trailing opposite the direction of travel.
    const nx = dx / d;
    const ny = dy / d;
    if (state.modernEnabled) {
      spawnBurst(rd, t.x - nx * rd.tankR * 1.2, t.y - ny * rd.tankR * 1.2, 1, nowMs, {
        color: "rgba(0, 248, 248, 0.4)",
        speed: 8,
        size: 2.5,
        life: 250,
        alpha: 0.4,
        drag: 1,
        grow: 0.2, // shrink
        vx: -nx * 12,
        vy: -ny * 12,
      });
    } else {
      spawnBurst(rd, t.x - nx * rd.tankR * 1.2, t.y - ny * rd.tankR * 1.2, 1, nowMs, {
        color: "#8a7f68",
        speed: 8,
        size: 3,
        life: 480,
        alpha: 0.3,
        drag: 1,
        grow: 2.2,
        vx: -nx * 12,
        vy: -ny * 12,
      });
    }
    last.x = t.x;
    last.y = t.y;
  }
}

/** Flash the DOM damage vignette over the arena (local player hurt/killed). */
export function flashVignette(strong: boolean): void {
  const el = $("damage-vignette");
  if (!el) return;
  el.classList.remove("dmg-flash", "dmg-flash-strong");
  void el.offsetWidth; // restart the CSS animation
  el.classList.add(strong ? "dmg-flash-strong" : "dmg-flash");
}

/** Per-tank transient overlays: muzzle flash and the white hit-flash ring. */
export function drawTankFx(rd: Renderer, t: TankDTO, nowMs: number): void {
  const f = rd.tankFx.get(t.id);
  if (!f || !t.alive) return;
  const { ctx } = rd;
  const r = rd.tankR;

  if (nowMs < f.muzzleUntil) {
    const a = (f.muzzleUntil - nowMs) / MUZZLE_MS; // 1 → 0
    ctx.save();
    ctx.translate(t.x, t.y);
    ctx.rotate(t.turretAngle);
    ctx.translate(r + 10, 0);
    ctx.globalAlpha = a;
    // Four-petal star: long forward tongue, short side/back petals.
    const L = 7 + 5 * a;
    ctx.fillStyle = "#ffd24a";
    ctx.beginPath();
    ctx.moveTo(L, 0);
    ctx.lineTo(2, 2.6);
    ctx.lineTo(0, L * 0.6);
    ctx.lineTo(-2, 2.6);
    ctx.lineTo(-L * 0.45, 0);
    ctx.lineTo(-2, -2.6);
    ctx.lineTo(0, -L * 0.6);
    ctx.lineTo(2, -2.6);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "#fff6d8";
    ctx.beginPath();
    ctx.arc(0, 0, 2.5 + 1.5 * a, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  const hAge = (nowMs - f.hitAt) / HIT_FLASH_MS;
  if (f.hitAt > 0 && hAge < 1) {
    ctx.save();
    ctx.globalAlpha = (1 - hAge) * 0.8;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2.5 * (1 - hAge) + 0.5;
    ctx.beginPath();
    ctx.arc(t.x, t.y, r + 3 + hAge * 8, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
    ctx.globalAlpha = 1;
  }
}
